import { useState, useEffect, lazy, Suspense } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { useNavigate } from "react-router-dom";
import { Flag, Users, Trophy, TrendingUp, HelpCircle } from "lucide-react";
import { useAuth } from "@/hooks/useAuth";
import { OnboardingScreen } from "@/components/onboarding/OnboardingScreen";
const SettingsSheet = lazy(() => import("@/components/settings/SettingsSheet").then(m => ({ default: m.SettingsSheet })));
const ONBOARDING_KEY = "settleup_onboarding_completed";
const Index = () => {
  const navigate = useNavigate();
  const {
    isAuthenticated,
    loading
  } = useAuth();
  const [joinCode, setJoinCode] = useState("");
  const [showJoinInput, setShowJoinInput] = useState(false);
  const [showOnboarding, setShowOnboarding] = useState(false);

  // Check if user has completed onboarding
  useEffect(() => {
    if (!loading) {
      const hasCompletedOnboarding = localStorage.getItem(ONBOARDING_KEY);
      if (!hasCompletedOnboarding) {
        setShowOnboarding(true);
      }
    }
  }, [loading]);
  const handleCompleteOnboarding = () => {
    localStorage.setItem(ONBOARDING_KEY, "true");
    setShowOnboarding(false);
  };
  const handleJoinRound = () => {
    if (joinCode.trim().length >= 4) {
      navigate(`/round/${joinCode.toUpperCase()}`);
    }
  };

  // Show onboarding for first-time users
  if (showOnboarding) {
    return <OnboardingScreen onComplete={handleCompleteOnboarding} />;
  }
  return <div className="min-h-screen bg-background flex flex-col">
      {/* Header */}
      <header className="px-6 pt-10 pb-6 text-center relative">
        {/* Settings button in top right */}
        <div className="absolute top-5 right-5">
          <Suspense fallback={null}>
            <SettingsSheet />
          </Suspense>
        </div>

        <div className="flex items-baseline justify-center gap-2">
          <h1 className="text-4xl font-bold text-foreground leading-none">SettleUp</h1>
          <span className="text-4xl leading-none">⛳</span>
        </div>
        <p className="text-base text-muted-foreground/80 tracking-wide mt-3">
          Track your friendly competition on the course
        </p>
      </header>

      {/* Main Actions */}
      <main className="flex-1 px-6 pb-8">
        <div className="max-w-md mx-auto space-y-4">
          {/* Start New Round */}
          <Card className="overflow-hidden border-2 border-primary/20 shadow-lg hover:shadow-xl transition-shadow">
            <CardContent className="p-0">
              <Button onClick={() => navigate("/new-round")} className="w-full h-auto py-8 px-6 bg-primary hover:bg-primary/90 rounded-none flex flex-col items-center gap-3">
                <Flag className="w-12 h-12" />
                <div className="text-center">
                  <div className="text-2xl font-bold">Start New Round</div>
                  <div className="text-primary-foreground/80 text-sm mt-1">
                    Create a round and invite friends
                  </div>
                </div>
              </Button>
            </CardContent>
          </Card>

          {/* Join Round */}
          <Card className="overflow-hidden border-2 border-secondary/30 shadow-lg">
            <CardContent className="p-0">
              {!showJoinInput ? <Button onClick={() => setShowJoinInput(true)} variant="secondary" className="w-full h-auto py-8 px-6 rounded-none flex flex-col items-center gap-3">
                  <Users className="w-12 h-12" />
                  <div className="text-center">
                    <div className="text-2xl font-bold">Join Round</div>
                    <div className="text-secondary-foreground/80 text-sm mt-1">
                      Enter a room code to join
                    </div>
                  </div>
                </Button> : <div className="p-6 bg-secondary/10">
                  <div className="flex flex-col gap-4">
                    <div className="flex items-center gap-2">
                      <Users className="w-8 h-8 text-secondary-foreground" />
                      <span className="text-lg font-semibold text-foreground">Enter Room Code</span>
                    </div>
                    <Input value={joinCode} onChange={e => setJoinCode(e.target.value.toUpperCase())} placeholder="ABC123" className="text-center text-2xl font-mono tracking-widest h-14 uppercase" maxLength={6} autoFocus />
                    <div className="flex gap-3">
                      <Button variant="outline" onClick={() => {
                    setShowJoinInput(false);
                    setJoinCode("");
                  }} className="flex-1">
                        Cancel
                      </Button>
                      <Button onClick={handleJoinRound} disabled={joinCode.length < 4} className="flex-1 bg-secondary text-secondary-foreground hover:bg-secondary/90">
                        Join
                      </Button>
                    </div>
                  </div>
                </div>}
            </CardContent>
          </Card>

          {/* Quick Stats Preview */}
          <div className="grid grid-cols-2 gap-4 pt-4">
            <Card className="p-4 text-center hover:shadow-md transition-shadow cursor-pointer" onClick={() => navigate("/stats")}>
              <TrendingUp className="w-8 h-8 mx-auto mb-2 text-accent" />
              <div className="font-semibold text-foreground">My Stats</div>
              <div className="text-sm text-muted-foreground">View history</div>
            </Card>
            <Card className="p-4 text-center hover:shadow-md transition-shadow cursor-pointer" onClick={() => navigate("/leaderboard")}>
              <Trophy className="w-8 h-8 mx-auto mb-2 text-secondary" />
              <div className="font-semibold text-foreground">Leaderboard</div>
              <div className="text-sm text-muted-foreground">Friend rankings</div>
            </Card>
          </div>

          {/* Game Info Link */}
          <Card className="p-4 text-center hover:shadow-md transition-shadow cursor-pointer border-dashed border-2" onClick={() => navigate("/game-info")}>
            <div className="flex items-center justify-center gap-3">
              <HelpCircle className="w-6 h-6 text-muted-foreground" />
              <div>
                <div className="font-semibold text-foreground">Learn the Games</div>
                <div className="text-sm text-muted-foreground">How to play each betting game</div>
              </div>
            </div>
          </Card>
        </div>
      </main>

      {/* Footer */}
      <footer className="px-6 py-4 text-center">
        <p className="text-xs text-muted-foreground">
          Track games, settle bets, have fun! 🏌️‍♂️
        </p>
      </footer>
    </div>;
};
export default Index;